import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { toast } from '../../../../utils/toast';
import { useTranslation } from 'react-i18next';
import CommonModal from '../../../common/CommonModal';
import { Form, Field, withFormik } from 'formik';
import Typography from '../../../common/StyledTypography';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
  FormikRadioGroup,
  FormikAutocomplete,
} from '../../../common/FormikComponent';
import axiosInstance from '../../../../axiosInstance';
import Radio from 'apollo-react/components/Radio';
import MenuItem from 'apollo-react/components/MenuItem';
import OrderedMultiSelect from '../../../common/Form/OrderedMultiSelect';

import validate from './validators/stageConfigValidation';
import {
  NODE_CATEGORIES,
  STAGE_TYPES,
} from '../../../../constants/workflow.constant';
import useOrganizationHierarchicalSelect from '../../../../hooks/features/workflows/useOrganizationHierarchicalSelect';
import { useWorkflow } from './WorkflowContext';
import modalStyles from './StageConfigModal.module.css';

const initialValues = {
  name: '',
  description: '',
  form_id: '',
  form_ids: [], // For multi-form review/submission stages
  form_names: {}, // Map of form_id to form name for validation messages
  stage_type_id: 1,
  notification_type_id: '',
  target_category_id: '',
  approver_role_id: '',
  sla_enabled: false,
  sla_duration: '',
  action: 'form',
  nodeId: '',
  subflow_id: '',
  users: [],
  organizationIds: [],
  departmentIds: [],
  userIds: [], // Selected user IDs with hierarchy tracking
  targetingMode: '', // '' = all users, 'org' = org hierarchy, 'standalone' = standalone users
  standaloneUsers: [],
  standaloneUserIds: [],
  payment_enabled: false,
  payment_amount: '',
};

const handleSubmit = (values, { setSubmitting }) => {
  setSubmitting(false);
};

const BuilderStagesModal = ({
  nodes,
  nodeStageId,
  content,
  onEvent,
  onConfirm,
  onDiscard,
  usedSubmissionFormIds = [],
  workflowSubmissionFormIds = [],
  upstreamSubmissionFormIds = [],
  isNodeConnected = false,
  paymentProviderId,
  paymentCurrency,
  paymentEnabled,
  setFieldTouched,
  setFieldError,
  values,
  setFieldValue,
}) => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const { edges } = useWorkflow();
  const [slaEnabled, setSlaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [formsSelection, setFormsSelection] = useState([]);
  const [notificationTypeSelection, setNotificationTypeSelection] = useState(
    []
  );
  const [categoriesSelection, setCategoriesSelection] = useState([]);
  const [rolesSelection, setRolesSelection] = useState([]);
  const [stageTypesSelection, setStageTypesSelection] = useState([]);
  const [slaLabel, setSlaLabel] = useState(t('stageConfig.slaResubmit'));
  const [usersSelection, setUsersSelection] = useState([]);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [standaloneUsersData, setStandaloneUsersData] = useState([]);
  const paymentReady = Boolean(
    paymentEnabled && paymentProviderId && paymentCurrency
  );

  // Hierarchical selection hook for Organization → Department → User
  // Pass specific arrays to avoid re-renders when unrelated Formik fields change
  const {
    availableOrganizations,
    availableDepartments,
    availableUsers,
    selectedOrgIds,
    selectedDeptIds,
    selectedUserIds,
    handleOrgsChange,
    handleDeptsChange,
    handleUsersChange,
    fullSelection,
  } = useOrganizationHierarchicalSelect(
    organizationsData,
    values.organizationIds,
    values.departmentIds,
    values.userIds,
    setFieldValue
  );
  // Track if we've initialized to prevent resetting on content changes
  const initializedRef = useRef(false);
  const lastNodeStageIdRef = useRef(null);

  const isCurrentStageAssessmentReview = nodes.find(
    (node) =>
      node.id === nodeStageId &&
      node?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW
  );

  useEffect(() => {
    // Only initialize when modal opens with a new node, or when nodeStageId changes
    const isNewNode = lastNodeStageIdRef.current !== nodeStageId;

    if (isNewNode || !initializedRef.current) {
      setLoading(true);
      initializedRef.current = true;
      lastNodeStageIdRef.current = nodeStageId;

      // If update, populate the form with existing content
      if (content) {
        Object.keys(content).forEach((key) => {
          // eslint-disable-next-line no-prototype-builtins
          if (initialValues.hasOwnProperty(key)) {
            setFieldValue(key, content[key]);
          }
        });

        // Ensure form_ids is properly set as array
        if (content.form_ids && Array.isArray(content.form_ids)) {
          setFieldValue('form_ids', content.form_ids);
        } else if (content.form_id && !content.form_ids) {
          // If only form_id exists, initialize form_ids with it for Review stages
          setFieldValue('form_ids', [content.form_id]);
        }

        // Synchronize the slaEnabled state with the sla_enabled value
        if (content.sla_enabled !== undefined) {
          setSlaEnabled(content.sla_enabled);
        }

        // infer targeting mode from saved data
        if (!content.targetingMode && content.target_category_id) {
          if (content.standaloneUsers && content.standaloneUsers.length > 0) {
            setFieldValue('targetingMode', 'standalone');
            setFieldValue(
              'standaloneUserIds',
              content.standaloneUsers.map((u) => u.id)
            );
          } else if (
            content.organizations &&
            content.organizations.length > 0
          ) {
            setFieldValue('targetingMode', 'org');
          } else {
            setFieldValue('targetingMode', '');
          }
        }
      }

      if (nodeStageId) {
        setFieldValue('nodeId', nodeStageId);
      }

      setLoading(false);
    }
  }, [nodeStageId, content, setFieldValue]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!showModal) {
      initializedRef.current = false;
      lastNodeStageIdRef.current = null;
    }
  }, [showModal]);

  // for assessment review stages: always derive form_ids from the connected review node
  useEffect(() => {
    if (!nodeStageId) return;
    const arNode = nodes.find(
      (n) =>
        n.id === nodeStageId && n.category === NODE_CATEGORIES.ASSESSMENT_REVIEW
    );
    if (!arNode) return;
    // traverse: AR ← (edge) ← CHILD_STA ← (edge) ← Review node
    const staEdge = edges.find((e) => e.target === nodeStageId);
    if (!staEdge) return;
    const reviewEdge = edges.find((e) => e.target === staEdge.source);
    if (!reviewEdge) return;
    const reviewNode = nodes.find((n) => n.id === reviewEdge.source);
    if (!reviewNode?.data?.formConfig) return;
    const fc = reviewNode.data.formConfig;
    const formIds = fc.form_ids?.length
      ? fc.form_ids
      : fc.form_id
        ? [fc.form_id]
        : [];
    setFieldValue('form_ids', formIds);
    setFieldValue('form_id', formIds[0] || '');
    setFieldValue('form_names', fc.form_names || {});
  }, [nodeStageId, edges, nodes]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [
          formsResponse,
          notificationResponse,
          categoriesResponse,
          rolesResponse,
          StageTypesResponse,
        ] = await Promise.all([
          axiosInstance.get('/api/forms/private'),
          axiosInstance.get('/api/thresholds/notification-types'),
          axiosInstance.get('/api/categories/public'),
          axiosInstance.get('/api/roles/reviews/assigned'),
          axiosInstance.get('/api/stage_types'),
        ]);

        setFormsSelection(formsResponse.data);
        setCategoriesSelection(categoriesResponse.data);
        setNotificationTypeSelection(notificationResponse.data);
        setRolesSelection(rolesResponse.data);

        // If current stage is an assessment review stage, set the stage type to assessment review
        // Else hide assessment review stage type
        let stageTypes = StageTypesResponse.data;
        if (isCurrentStageAssessmentReview) {
          stageTypes = stageTypes.filter(
            (stageType) => stageType.type === STAGE_TYPES.ASSESSMENT_REVIEW
          );
          if (stageTypes.length > 0)
            setFieldValue('stage_type_id', stageTypes[0].id);
        } else {
          stageTypes = stageTypes.filter(
            (stageType) => stageType.type !== STAGE_TYPES.ASSESSMENT_REVIEW
          );
        }
        setStageTypesSelection(stageTypes);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoleUsers = useCallback(async (roleId) => {
    try {
      setFetchingData(true);
      const response = await axiosInstance.get(
        `/api/roles/role-users-reviews/${roleId}`
      );
      setUsersSelection(
        response.data.map((user) => {
          return {
            label: user.username,
            value: user.id,
            ...user,
          };
        })
      );
    } catch (error) {
      console.error('Error fetching role users:', error);
    } finally {
      setFetchingData(false);
    }
  }, []);

  // Fetch the users for the approver role
  useEffect(() => {
    if (values.approver_role_id) {
      fetchRoleUsers(values.approver_role_id);
    }
  }, [values.approver_role_id, fetchRoleUsers]);

  // reconcile previously-saved users with freshly-fetched options so the
  // autocomplete shows them as selected (apollo-react compares by reference)
  useEffect(() => {
    if (!usersSelection.length || !values.users?.length) return;
    const reconciled = values.users
      .map((u) => usersSelection.find((opt) => opt.value === (u.value ?? u.id)))
      .filter(Boolean);
    const sameLength = reconciled.length === values.users.length;
    const sameRefs =
      sameLength && reconciled.every((opt, i) => opt === values.users[i]);
    if (!sameRefs) {
      setFieldValue('users', reconciled);
    }
  }, [usersSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganizationsDataByCategory = useCallback(async (categoryId) => {
    try {
      setFetchingData(true);
      const response = await axiosInstance.get(
        `/api/v1/organizations?categoryId=${categoryId}&includeDepartmentUsers=true`
      );
      if (response?.data) {
        const organizations = response?.data?.data || [];
        setOrganizationsData(organizations);
      }
    } catch (error) {
      console.error('Error fetching organizations', error);
      setOrganizationsData([]);
    } finally {
      setFetchingData(false);
    }
  }, []);

  // Fetch the organizations for the categories
  useEffect(() => {
    if (values.target_category_id) {
      fetchOrganizationsDataByCategory(values.target_category_id);
    }
  }, [values.target_category_id, fetchOrganizationsDataByCategory]);

  const fetchStandaloneUsers = useCallback(async (categoryId) => {
    try {
      setFetchingData(true);
      const response = await axiosInstance.get(
        `/api/endUsers/standalone/${categoryId}`
      );
      setStandaloneUsersData(response.data || []);
    } catch {
      setStandaloneUsersData([]);
    } finally {
      setFetchingData(false);
    }
  }, []);

  useEffect(() => {
    if (values.target_category_id && values.targetingMode === 'standalone') {
      fetchStandaloneUsers(values.target_category_id);
    }
  }, [values.target_category_id, values.targetingMode, fetchStandaloneUsers]);

  useEffect(() => {
    if (onEvent) {
      updateSlaLabel(1);

      setShowModal(true);
    }
  }, [nodes, onEvent]);

  useEffect(() => {
    if (!paymentReady && values.payment_enabled) {
      setFieldValue('payment_enabled', false);
      setFieldValue('payment_amount', '');
    }
  }, [paymentReady, setFieldValue, values.payment_enabled]);

  // Check if it is the first submission in flow (To set "SLA_Enabled" OR "SLA of resubmitting after send back")
  const updateSlaLabel = (current_stage_type_id) => {
    // If workflow already has a submission node
    const getSubmissionNodesExists = nodes.some(
      (node) => node.category === NODE_CATEGORIES.SUBMISSION
    );

    if (getSubmissionNodesExists) {
      setSlaLabel(t('stageConfig.slaEnabled'));
    }
  };

  const handleCloseModal = () => {
    onDiscard(nodeStageId);

    setShowModal(false);
    onEvent({ open: false, sentData: null });
  };

  const slaAvail = (event, setFieldValue) => {
    const { checked } = event.target;
    setSlaEnabled(checked);
    setFieldValue('sla_enabled', checked);
  };

  const actionTypeChange = (event, setFieldValue) => {
    const { value } = event.target;
    setFieldValue('stage_type_id', parseInt(value, 10));
    setFieldValue('approver_role_id', '');
    setFieldValue('users', []); // Reset the users selection
    setFieldValue('organizationIds', []);
    setFieldValue('departmentIds', []);
    setFieldValue('userIds', []);
    setFieldValue('targetingMode', '');
    setFieldValue('standaloneUsers', []);
    setFieldValue('standaloneUserIds', []);

    const selectedStageType = stageTypesSelection.find(
      (stageType) => stageType.id === parseInt(value, 10)
    );

    if (selectedStageType?.type !== STAGE_TYPES.SUBMISSION) {
      setFieldValue('target_category_id', '');
      setFieldValue('payment_enabled', false);
      setFieldValue('payment_amount', '');
    }

    setFieldValue('form_id', '');
    setFieldValue('form_ids', []);

    updateSlaLabel(value);
  };

  // Check if current stage type is Review
  const isReviewStage = () => {
    const selectedStageType = stageTypesSelection.find(
      (stageType) => stageType.id === values.stage_type_id
    );
    return selectedStageType?.type === STAGE_TYPES.REVIEW;
  };

  // Check if current stage type is Submission
  const isSubmissionStage = () => {
    const selectedStageType = stageTypesSelection.find(
      (stageType) => stageType.id === values.stage_type_id
    );
    return selectedStageType?.type === STAGE_TYPES.SUBMISSION;
  };

  // Check if current stage type is Assessment Review
  const isAssessmentReviewStage = () => {
    const selectedStageType = stageTypesSelection.find(
      (stageType) => stageType.id === values.stage_type_id
    );
    return selectedStageType?.type === STAGE_TYPES.ASSESSMENT_REVIEW;
  };

  // Get forms available for Review stages
  // If node is connected: show only upstream submission forms (better UX)
  // If node is NOT connected: show all workflow submission forms (validation on connect)
  // IMPORTANT: Always include currently selected forms to prevent automatic removal
  const reviewableForms = useMemo(() => {
    const selectedStageType = stageTypesSelection.find(
      (stageType) => stageType.id === values.stage_type_id
    );
    const isReview =
      selectedStageType?.type === STAGE_TYPES.REVIEW ||
      selectedStageType?.type === STAGE_TYPES.ASSESSMENT_REVIEW;

    if (!isReview) {
      return [];
    }

    // If no submission stages exist yet, show empty (user must create submission first)
    if (workflowSubmissionFormIds.length === 0) {
      return [];
    }

    // Get currently selected form IDs to preserve them
    const currentFormIds = values.form_ids || [];

    // Base available forms based on connection status
    let availableForms;
    if (isNodeConnected && upstreamSubmissionFormIds.length > 0) {
      // If node is connected, show forms from upstream submissions
      availableForms = formsSelection.filter((form) =>
        upstreamSubmissionFormIds.includes(form.id)
      );
    } else {
      // If node is not connected, show all workflow submission forms
      // Validation will happen when the node is connected
      availableForms = formsSelection.filter((form) =>
        workflowSubmissionFormIds.includes(form.id)
      );
    }

    // IMPORTANT: Always include currently selected forms, even if they're not upstream
    // This prevents automatic removal when forms are removed from submission nodes
    const selectedFormsNotInAvailable = currentFormIds
      .map((formId) => formsSelection.find((f) => f.id === formId))
      .filter(Boolean)
      .filter((form) => !availableForms.find((f) => f.id === form.id));

    // Combine available forms with currently selected forms that aren't in available
    return [...availableForms, ...selectedFormsNotInAvailable];
  }, [
    formsSelection,
    values.stage_type_id,
    values.form_ids, // Include current form_ids to preserve selections
    stageTypesSelection,
    workflowSubmissionFormIds,
    upstreamSubmissionFormIds,
    isNodeConnected,
  ]);

  // Get forms available for Submission stages (excluding already used forms)
  const submissionForms = useMemo(() => {
    return formsSelection.filter(
      (form) => !usedSubmissionFormIds.includes(form.id)
    );
  }, [formsSelection, usedSubmissionFormIds]);

  // Handle ordered multi-select form change for Review/Submission stages
  const handleFormIdsChange = (orderedIds) => {
    setFieldValue('form_ids', orderedIds);
    // Build a map of form ID to name for validation messages
    const formNamesMap = {};
    orderedIds.forEach((id) => {
      const form = formsSelection.find((f) => f.id === id);
      if (form) {
        formNamesMap[id] = form.name;
      }
    });
    setFieldValue('form_names', formNamesMap);
    // Also set first form as primary form_id for backward compatibility
    if (orderedIds.length > 0) {
      setFieldValue('form_id', orderedIds[0]);
    } else {
      setFieldValue('form_id', '');
    }
  };

  const handleSave = async (validate, values) => {
    const existingNames = nodes
      .filter((node) => node.id !== nodeStageId && node.data?.formConfig?.name)
      .map((node) => node.data.formConfig.name);
    const errors = await validate({
      ...values,
      stageTypesSelection,
      existingNames,
    });

    if (Object.keys(errors).length === 0) {
      toast.success(t('stageConfig.stageAddedSuccess'));
      const isSubmission = !!values.target_category_id;
      const isOrgMode = values.targetingMode === 'org';
      const isStandaloneMode = values.targetingMode === 'standalone';
      const isReviewLike = isReviewStage() || isAssessmentReviewStage();

      // for submission stages: users come from the org-hierarchy hook
      // for review / assessment review stages: users come straight from values.users
      let usersToSave = [];
      if (isSubmission) {
        usersToSave = isOrgMode ? fullSelection.users : [];
      } else if (isReviewLike) {
        usersToSave = values.users || [];
      }

      const selectedStandalone = isStandaloneMode
        ? standaloneUsersData.filter((u) =>
            values.standaloneUserIds.includes(u.id)
          )
        : [];

      onConfirm(nodeStageId, {
        ...values,
        organizations: isOrgMode ? fullSelection.organizations : [],
        departments: isOrgMode ? fullSelection.departments : [],
        users: usersToSave,
        standaloneUsers: selectedStandalone,
      });
      setShowModal(false);
    } else {
      Object.keys(errors).forEach((field) => {
        setFieldTouched(field, true, false);
        setFieldError(field, errors[field]);
      });
    }
  };

  return (
    <>
      <CommonModal
        open={showModal}
        variant="default"
        title={t('stageConfig.addStage')}
        id="stage_creation"
        onClose={() => handleCloseModal()}
        buttonProps={[
          { label: t('stageConfig.goBack'), onClick: handleCloseModal },
          {
            label: t('stageConfig.save'),
            onClick: () =>
              handleSave(validate, values, setFieldTouched, setFieldError),
          },
        ]}
      >
        {loading ? (
          <Typography
            variant="bodySmall"
            className={modalStyles.verticalMargin}
          >
            {t('stageConfig.loading')}
          </Typography>
        ) : (
          <Form noValidate>
            <FormikTextField
              fullWidth
              name="name"
              label={t('stageConfig.name')}
              key="name"
              required
              autoFocus
            />

            <FormikTextField
              fullWidth
              name="description"
              label={t('Description')}
              key="description"
              multiline
              sizeAdjustable
            />

            <FormikRadioGroup
              label={t('Action type')}
              aria-label="label"
              name="stage_type_id"
              key={values.stage_type_id}
              value={values.stage_type_id}
              onChange={(e) => actionTypeChange(e, setFieldValue)}
            >
              {stageTypesSelection?.map((stageType) => (
                <Radio
                  key={stageType.id}
                  value={stageType.id}
                  label={stageType.type}
                />
              ))}
            </FormikRadioGroup>

            {/* Category or Role based on selection */}
            {stageTypesSelection?.map((stageType) =>
              stageType.type === STAGE_TYPES.SUBMISSION &&
              values.stage_type_id === stageType.id ? (
                <>
                  <FormikSelect
                    name="target_category_id"
                    label={t('End-User Category')}
                    key="target_category_id"
                    fullWidth
                    required
                    onChange={(e) => {
                      setFieldValue('target_category_id', e.target.value);
                      setFieldValue('organizationIds', []);
                      setFieldValue('departmentIds', []);
                      setFieldValue('userIds', []);
                      setFieldValue('users', []);
                      setFieldValue('targetingMode', '');
                      setFieldValue('standaloneUsers', []);
                      setFieldValue('standaloneUserIds', []);
                    }}
                  >
                    <MenuItem value="">
                      {t('stageConfig.selectCategory')}
                    </MenuItem>
                    {categoriesSelection?.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </FormikSelect>

                  {fetchingData && (
                    <Typography
                      variant="bodySmall"
                      className={modalStyles.verticalMargin}
                    >
                      {t('stageConfig.loading')}
                    </Typography>
                  )}

                  {values.target_category_id && (
                    <FormikRadioGroup
                      label={t('stageConfig.userTargeting')}
                      name="targetingMode"
                      value={values.targetingMode}
                      onChange={(e) => {
                        setFieldValue('targetingMode', e.target.value);
                        setFieldValue('organizationIds', []);
                        setFieldValue('departmentIds', []);
                        setFieldValue('userIds', []);
                        setFieldValue('standaloneUsers', []);
                        setFieldValue('standaloneUserIds', []);
                      }}
                    >
                      <Radio value="org" label={t('yes')} />
                      <Radio value="standalone" label={t('no')} />
                    </FormikRadioGroup>
                  )}

                  {values.targetingMode === 'org' &&
                    values.target_category_id &&
                    availableOrganizations.length > 0 && (
                      <OrderedMultiSelect
                        label={t('organizations')}
                        placeholder={t('search_organizations')}
                        options={availableOrganizations}
                        value={selectedOrgIds}
                        onChange={handleOrgsChange}
                        getOptionLabel={(org) => org.name}
                      />
                    )}

                  {values.targetingMode === 'org' &&
                    values.organizationIds.length > 0 &&
                    availableDepartments.length > 0 && (
                      <OrderedMultiSelect
                        label={t('departments')}
                        placeholder={t('search_departments')}
                        options={availableDepartments}
                        value={selectedDeptIds}
                        onChange={handleDeptsChange}
                        getOptionLabel={(dept) => dept.name}
                      />
                    )}

                  {values.targetingMode === 'org' &&
                    values.departmentIds.length > 0 &&
                    availableUsers.length > 0 && (
                      <OrderedMultiSelect
                        label={t('users')}
                        placeholder={t('search_users')}
                        options={availableUsers}
                        value={selectedUserIds}
                        onChange={handleUsersChange}
                        getOptionLabel={(user) => `${user.username}`}
                      />
                    )}

                  {values.targetingMode === 'standalone' &&
                    values.target_category_id &&
                    standaloneUsersData.length > 0 && (
                      <OrderedMultiSelect
                        label={t('users')}
                        placeholder={t('search_users')}
                        options={standaloneUsersData}
                        value={values.standaloneUserIds}
                        onChange={(orderedIds) => {
                          setFieldValue('standaloneUserIds', orderedIds);
                        }}
                        getOptionLabel={(user) =>
                          user.first_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.username
                        }
                      />
                    )}
                </>
              ) : (stageType.type === STAGE_TYPES.REVIEW &&
                  values.stage_type_id === stageType.id) ||
                (stageType.type === STAGE_TYPES.ASSESSMENT_REVIEW &&
                  values.stage_type_id === stageType.id) ? (
                <FormikSelect
                  name="approver_role_id"
                  label={t('internal_role')}
                  key="approver_role_id"
                  fullWidth
                  required
                  onChange={(e) => {
                    setFieldValue('approver_role_id', e.target.value);
                    setFieldValue('users', []);
                  }}
                >
                  <MenuItem value="">{t('stageConfig.selectRole')}</MenuItem>
                  {rolesSelection?.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </FormikSelect>
              ) : null
            )}

            {fetchingData && (
              <Typography
                variant="bodySmall"
                className={modalStyles.verticalMargin}
              >
                {t('stageConfig.loading')}
              </Typography>
            )}

            {(isReviewStage() || isAssessmentReviewStage()) &&
              values.approver_role_id &&
              usersSelection.length > 0 && (
                <FormikAutocomplete
                  label={t('users')}
                  placeholder={t('search_users')}
                  source={usersSelection}
                  value={values.users}
                  onChange={(event, newValue) => {
                    setFieldValue('users', newValue);
                  }}
                  multiple
                  fullWidth
                  isField={true}
                />
              )}

            {/* Form selection - Multi-select for both Submission and Review stages */}
            {isSubmissionStage() ? (
              <OrderedMultiSelect
                label={t('Forms to Submit')}
                helperText={
                  submissionForms.length === 0
                    ? t('No available forms')
                    : t('Select forms in the order to be filled')
                }
                placeholder={t('Search forms...')}
                options={submissionForms}
                value={values.form_ids || []}
                onChange={handleFormIdsChange}
                required
                error={submissionForms.length === 0}
                getOptionLabel={(form) => form.name}
              />
            ) : isReviewStage() ? (
              <OrderedMultiSelect
                label={t('Forms to Review')}
                helperText={
                  reviewableForms.length === 0
                    ? t('workflowBuilder.addSubmissionFirst')
                    : isNodeConnected
                      ? t('workflowBuilder.upstreamFormsOnly')
                      : t('workflowBuilder.allFormsValidateOnConnect')
                }
                placeholder={t('Search forms...')}
                options={reviewableForms}
                value={values.form_ids || []}
                onChange={handleFormIdsChange}
                required
                error={reviewableForms.length === 0}
                getOptionLabel={(form) => form.name}
                className={modalStyles.autocomplete}
              />
            ) : isAssessmentReviewStage() ? (
              <OrderedMultiSelect
                label={t('Forms to Review')}
                helperText={t('workflowBuilder.formsInheritedFromReview')}
                placeholder={t('Search forms...')}
                options={reviewableForms}
                value={values.form_ids || []}
                onChange={handleFormIdsChange}
                getOptionLabel={(form) => form.name}
                className={modalStyles.autocomplete}
                disabled
              />
            ) : null}

            {isSubmissionStage() && (
              <>
                <FormikSwitch
                  name="payment_enabled"
                  checked={values.payment_enabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setFieldValue('payment_enabled', enabled);
                    if (!enabled) {
                      setFieldValue('payment_amount', '');
                    }
                  }}
                  label={t('stageConfig.paymentEnabled')}
                  key="payment_enabled"
                  disabled={!paymentReady}
                />

                {values.payment_enabled && (
                  <>
                    <div className={modalStyles.paymentCurrencyRow}>
                      <Typography
                        variant="bodySmall"
                        className={modalStyles.paymentCurrencyLabel}
                      >
                        {t('stageConfig.paymentCurrency')}
                      </Typography>
                      <Typography
                        variant="bodySmall"
                        className={modalStyles.paymentCurrencyValue}
                      >
                        {paymentCurrency || '-'}
                      </Typography>
                    </div>

                    <FormikTextField
                      type="number"
                      fullWidth
                      name="payment_amount"
                      label={t('stageConfig.paymentAmount')}
                      key="payment_amount"
                      required
                    />
                  </>
                )}
              </>
            )}

            <Field type="hidden" name="action" key="action" value="form" />

            <Field type="hidden" name="nodeId" key="nodeId" />

            <Field type="hidden" name="subflow_id" value="" key="subflow_id" />

            <FormikSelect
              name="notification_type_id"
              label={t('stageConfig.notificationType')}
              key="notification_type_id"
              fullWidth
            >
              <MenuItem value="">
                {t('stageConfig.selectNotificationType')}
              </MenuItem>
              {notificationTypeSelection?.map((notification) => (
                <MenuItem key={notification.id} value={notification.id}>
                  {notification.type}
                </MenuItem>
              ))}
            </FormikSelect>

            <FormikSwitch
              name="sla_enabled"
              checked={values.sla_enabled}
              onChange={(e) => slaAvail(e, setFieldValue)}
              // label change based on the first submission
              label={slaLabel}
              key="sla_enabled"
            />

            {slaEnabled && (
              <FormikTextField
                type="number"
                fullWidth
                name="sla_duration"
                label={t('stageConfig.slaDuration')}
              />
            )}
          </Form>
        )}
      </CommonModal>
    </>
  );
};

export default withFormik({
  validate,
  mapPropsToValues: () => initialValues,
  handleSubmit,
})(BuilderStagesModal);
